import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { ChevronDown, Globe } from 'lucide-react';

const PlatformSelector = () => {
  const { user, selectedPlatform, switchPlatform, isAdmin } = useAuth();
  const { platforms, currentPlatform } = usePlatform();
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  if (!user || platforms.length === 0) return null;

  return (
    <>
      {/* Platform Selector */}
      <div className="relative">
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          data-testid="platform-selector-trigger"
          className="flex items-center space-x-2 px-2.5 sm:px-4 py-2.5 bg-white border rounded-lg shadow-sm hover:bg-gray-50 transition-colors min-w-0 max-w-[150px] sm:max-w-none sm:min-w-[180px]"
        >
          {selectedPlatform === 'all' ? (
            <Globe className="h-5 w-5 text-gray-500 flex-shrink-0" />
          ) : currentPlatform?.favicon_url ? (
            <img 
              src={currentPlatform.favicon_url} 
              alt="" 
              className="h-5 w-5 flex-shrink-0 rounded"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : (
            <Globe className="h-5 w-5 text-gray-500 flex-shrink-0" />
          )}
          <Globe className="h-5 w-5 text-gray-500 flex-shrink-0 hidden" />
          <span className="text-sm font-semibold truncate flex-1 text-left">
            {selectedPlatform === 'all' ? '🌐 All Platforms' : (currentPlatform?.name || 'Select Platform')}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
        </button>

        {dropdownOpen && (
          <>
            {/* Backdrop for mobile */}
            <div
              className="fixed inset-0 bg-black/20 z-40 sm:hidden"
              onClick={() => setDropdownOpen(false)}
            />
            
            {/* Dropdown */}
            <div className="fixed sm:absolute left-4 right-4 sm:left-0 sm:right-auto top-20 sm:top-auto mt-0 sm:mt-2 w-auto sm:w-80 bg-white border rounded-lg shadow-xl z-50">
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    Available Platforms
                  </div>
                  {/* Close button for mobile */}
                  <button
                    onClick={() => setDropdownOpen(false)}
                    className="sm:hidden p-1 hover:bg-gray-100 rounded"
                  >
                    <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Scrollable container for platforms */}
                <div className="max-h-[calc(100vh-12rem)] sm:max-h-96 overflow-y-auto overscroll-contain"
                     style={{
                       scrollbarWidth: 'thin',
                       scrollbarColor: '#d1d5db #f3f4f6'
                     }}>
                  {/* All Platforms option for admin users */}
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => {
                          switchPlatform('all');
                          setDropdownOpen(false);
                        }}
                        data-testid="platform-option-all"
                        className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors ${
                          selectedPlatform === 'all'
                            ? 'bg-blue-100 text-blue-900'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="font-semibold">🌐 All Platforms</div>
                        <div className="text-xs text-gray-600 mt-0.5">View data from all platforms</div>
                        {selectedPlatform === 'all' && (
                          <div className="text-xs text-blue-600 font-medium mt-1">✓ Currently Selected</div>
                        )}
                      </button>
                      <div className="border-t border-gray-200 my-2"></div>
                    </>
                  )}
                  
                  {/* Sort platforms alphabetically before displaying */}
                  {platforms
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => {
                        switchPlatform(platform.id);
                        setDropdownOpen(false);
                      }}
                      data-testid={`platform-option-${platform.id}`}
                      className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors flex items-start gap-2 ${
                        selectedPlatform === platform.id
                          ? 'bg-blue-100 text-blue-900'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      {platform.favicon_url ? (
                        <img 
                          src={platform.favicon_url} 
                          alt="" 
                          className="h-5 w-5 flex-shrink-0 mt-0.5 rounded"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <Globe className="h-5 w-5 flex-shrink-0 mt-0.5 text-gray-400" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{platform.name}</div>
                        {platform.description && (
                          <div className="text-xs text-gray-600 mt-0.5 line-clamp-1">{platform.description}</div>
                        )}
                        {selectedPlatform === platform.id && (
                          <div className="text-xs text-blue-600 font-medium mt-1">✓ Current Platform</div>
                        )}
                      </div>
                    </button>
                  ))}
                  
                  {/* Scroll indicator - only show if there are many platforms */}
                  {platforms && platforms.length > 6 && (
                    <div className="text-center py-3 text-xs text-gray-400 border-t border-gray-100 mt-2">
                      <div className="flex items-center justify-center space-x-1 mb-1">
                        <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                        <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                        <div className="w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                      </div>
                      <div>Scroll for more</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Close dropdown when clicking outside - Desktop only */}
      {dropdownOpen && (
        <div
          className="hidden sm:block fixed inset-0 z-40"
          onClick={() => setDropdownOpen(false)}
        />
      )}
    </>
  );
};

export default PlatformSelector;